import type { ReviewMetrics } from '@dyspatch/core';
import { WAIT_TONE } from '@/components/common/bits';
import { Surface } from '@/components/ui/section';
import { duration, waitLevel, waitThresholds } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * The six numbers about review.
 *
 * It used to live on `/review`, which was a destination for something that is a
 * filter: pull requests waiting for a decision are gates, and the gates are all
 * on the home page now. The page went; these numbers did not, because the
 * project page reads them too and they answer a question nothing else does.
 *
 * Pickup and decision are the two that matter most: pickup is how long a PR sits
 * before anyone looks, decision is how long the looking takes. A queue can have a
 * healthy median wait and a terrible pickup, and only one of those is fixable by
 * hiring.
 */
export function ReviewMetricsStrip({
  metrics,
  className,
}: {
  metrics: ReviewMetrics;
  className?: string;
}) {
  // The hub's own stale interval, and the halfway mark every age on every page
  // turns amber at. See `waitThresholds`.
  const thresholds = waitThresholds(metrics.staleAfterMinutes);
  const oldestLevel = waitLevel(metrics.oldestWaitingMinutes, thresholds);
  const cells: { label: string; value: string | number; note?: string; tone?: string }[] = [
    {
      label: 'In review',
      value: metrics.inReview,
      note: metrics.inReview
        ? `median wait ${duration(metrics.medianWaitingMinutes)}`
        : 'queue clear',
      tone: metrics.inReview ? 'text-gate' : undefined,
    },
    {
      label: 'Oldest',
      value: metrics.inReview ? duration(metrics.oldestWaitingMinutes) : '-',
      note: 'the end that costs most',
      // Grey while the oldest PR is fresh: a tone on every tile at all times is
      // the same as no tone at all.
      tone: metrics.inReview && oldestLevel !== 'fresh' ? WAIT_TONE[oldestLevel] : undefined,
    },
    {
      label: `Stale > ${duration(thresholds.stale)}`,
      value: metrics.staleCount,
      note: `${duration(thresholds.stale)} unlooked at`,
      tone: metrics.staleCount ? 'text-warn' : undefined,
    },
    {
      label: 'Pickup',
      value: metrics.pickup.count ? duration(metrics.pickup.medianMinutes) : '-',
      note: metrics.pickup.count
        ? `median over ${metrics.pickup.count} · longest ${duration(metrics.pickup.longestMinutes)}`
        : 'no PR has been picked up yet',
    },
    {
      label: 'Decision',
      value: metrics.decision.count ? duration(metrics.decision.medianMinutes) : '-',
      note: metrics.decision.count
        ? `median over ${metrics.decision.count}, rework included`
        : 'nothing decided yet',
    },
    {
      label: 'Merged',
      value: metrics.merged.count,
      note: metrics.merged.count
        ? `median ${duration(metrics.merged.medianMinutes)} in review`
        : 'none yet',
      tone: metrics.merged.count ? 'text-ok' : undefined,
    },
  ];

  return (
    <Surface className={className}>
      <div className="divide-line grid grid-cols-2 divide-x divide-y sm:grid-cols-3 xl:grid-cols-6 xl:divide-y-0">
        {cells.map((cell) => (
          <div key={cell.label} className="min-w-0 px-gutter py-3.5">
            <div className="dy-eyebrow truncate">{cell.label}</div>
            <div className={cn('dy-figure mt-1.5 text-xl leading-none', cell.tone)}>
              {cell.value}
            </div>
            <div className="text-fg-3 mt-1.5 truncate text-2xs">{cell.note}</div>
          </div>
        ))}
      </div>
    </Surface>
  );
}
